/* eslint-disable quotes */
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Pagination, Popconfirm, Tooltip } from 'antd'
import moment from 'moment'
import SidePanel from '../../../components/common/side-panel/SidePanel'
import Spinner from '../../../components/common/spinner/Spinner'
import MainPanel from '../../../components/common/main-panel/MainPanel'
import {
  currentUserSubscription,
  getAllSubscriptionProducts,
  userSubscriptionUsage,
} from '../../../redux/actions/SubscriptionActions'
import { getAgreementTemplates } from '../../../redux/actions/AgreementTemplateActions'
import {
  copyTextToClipboard,
  generateRandomString,
} from '../../../utils/Helper'
import { toast } from 'react-toastify'
import instance from '../../../utils/Axios'

const itemsPerPage = 10

const itemRender = (_, type, originalElement) => {
  if (type === 'prev') {
    return <a>Prev</a>
  }
  if (type === 'next') {
    return <a>Next</a>
  }
  return originalElement
}

const DeveloperContent = ({ userConfig, setUserConfig }) => {
  const dispatch = useDispatch()
  const { agreementTemplates } = useSelector((state) => state)
  const [templateAgreementsData, setTemplateAgreementsData] = useState(
    agreementTemplates.data,
  )
  const [paginatedItems, setPaginatedItems] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [generateTokenLoading, setGenerateTokenLoading] = useState(false)
  const [curlCommand, setCurlCommand] = useState('')
  const [JSONPayload, setJSONPayload] = useState('')
  const [APIHeader, setAPIHeader] = useState('')

  const generateNewToken = async () => {
    setGenerateTokenLoading(true)
    // Generate access token
    const accessToken = generateRandomString(10)
    try {
      const response = await instance.post('/users/config', {
        accessToken,
      })
      setUserConfig(response?.data?.message)
    } catch (err) {
      toast.error('Unable to generate access token')
    } finally {
      setGenerateTokenLoading(false)
    }
  }

  // Invoke when user click to request another page.
  const handlePageClick = (value) => {
    setCurrentPage(value)
  }

  const getSubscriptionUsage = async () => {
    await dispatch(userSubscriptionUsage())
  }

  const loadUserSubscription = async () => {
    await dispatch(currentUserSubscription())
  }

  const loadAllSubscriptionProducts = async () => {
    await dispatch(getAllSubscriptionProducts())
  }

  const handleGetAgreementTemplates = async () => {
    await dispatch(getAgreementTemplates())
  }

  useEffect(() => {
    getSubscriptionUsage()
    loadUserSubscription()
    loadAllSubscriptionProducts()
    handleGetAgreementTemplates()
  }, [])

  useEffect(() => {
    setTemplateAgreementsData(agreementTemplates.data)
  }, [agreementTemplates.loading])

  useEffect(() => {
    const offset = (currentPage - 1) * itemsPerPage
    const paginatedTempAgreements = templateAgreementsData.slice(
      offset,
      offset + itemsPerPage,
    )
    setPaginatedItems(paginatedTempAgreements)
  }, [currentPage, templateAgreementsData])

  useEffect(() => {
    setCurlCommand(`curl -X POST ${process.env.REACT_APP_SERVER_URL}/api/user-agreements/external \
-H "Content-Type: application/json" \
-H "x-api-key: ${userConfig?.accessToken}" \
-d '{
  "template": "${templateAgreementsData?.[0]?.code}",
  "assets": ["https://tikbox1.s3.eu-west-2.amazonaws.com/assets/images/6800e1c340bf81d65771af7e/copyright_provenance_via_tikbox.io_tmpius9wumi_encoded.png"]
}'`)
    setJSONPayload(`{
  "template": "${templateAgreementsData?.[0]?.code}",
  "assets": ["https://tikbox1.s3.eu-west-2.amazonaws.com/assets/images/6800e1c340bf81d65771af7e/copyright_provenance_via_tikbox.io_tmpius9wumi_encoded.png"]
}`)
    setAPIHeader(`{
  "Content-Type": "application/json",
  "x-api-key": "${userConfig?.accessToken}"
}`)
  }, [userConfig, templateAgreementsData])

  return (
    <MainPanel>
      <div className="developer-container">
        <div className="title">
          <h2>Developer</h2>
        </div>
        <div className="developer-container__section">
          <div className="developer-templates">
            <h4>Agreement Templates</h4>

            <div className="developer-templates-table">
              <div className="developer-templates-table__header">
                <div className="developer-templates-table__row">
                  <div className="tikbox-col">
                    <span>Name</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Code</span>
                  </div>
                  <div className="tikbox-col">
                    <span>Date</span>
                  </div>
                </div>
              </div>
              <div className="developer-templates-table__body">
                {!paginatedItems.length ? (
                  <div style={{ textAlign: 'center' }}>No assets</div>
                ) : (
                  paginatedItems.map((agreementTemplate, index) => {
                    return (
                      <div
                        className="developer-templates-table__row"
                        key={index}>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Name</span>
                            <span>{agreementTemplate.templateName}</span>
                          </div>
                        </div>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Code</span>
                            <span>{agreementTemplate.code}</span>
                          </div>
                        </div>
                        <div className="tikbox-col">
                          <div className="tikbox-col__content">
                            <span className="title">Date</span>
                            <span>
                              {moment(agreementTemplate.date).format(
                                'DD/MM/YYYY',
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            {paginatedItems.length ? (
              <div className="developer-templates-pagination">
                <Pagination
                  current={currentPage}
                  onChange={handlePageClick}
                  total={templateAgreementsData.length}
                  showSizeChanger={false}
                  defaultPageSize={itemsPerPage}
                  itemRender={itemRender}
                />
              </div>
            ) : null}
          </div>
          <div className="developer-keys">
            <h4>Access Token</h4>

            <div className="developer-keys-section">
              <div className="developer-keys-section__content">
                <span>{userConfig?.accessToken}</span>
                <div className="action-buttons">
                  <button
                    className="copy"
                    onClick={() =>
                      copyTextToClipboard(userConfig?.accessToken)
                    }>
                    Copy
                  </button>
                  <Tooltip title="Generate new access token">
                    <Popconfirm
                      title="Regenerate access token"
                      description="Are you sure to regenerate access token?"
                      okText="Yes"
                      cancelText="No"
                      onConfirm={() => generateNewToken()}>
                      <button
                        disabled={generateTokenLoading}
                        className="generate">
                        {generateTokenLoading ? 'Generating...' : 'Generate'}
                      </button>
                    </Popconfirm>
                  </Tooltip>
                </div>
              </div>
            </div>

            <hr />
            <div className="developer-keys-doc">
              <h4>Documentation</h4>
              <p>
                You can use the access token to authenticate your requests to
                the Tikbox API. The access token should be included in the
                Authorization header of your requests.
              </p>
              <p>
                For example, to authenticate a request to the license endpoint,
                you can use the following curl command:
              </p>
              <div className="code-block">
                <span className="code-block__title">
                  <b>cURL</b>
                </span>
                <button
                  className="copy"
                  onClick={() => copyTextToClipboard(curlCommand)}>
                  Copy
                </button>
                <pre>
                  <code>
                    {`
${curlCommand}
                    `}
                  </code>
                </pre>
              </div>
              <p>
                For example, to authenticate a request to the license endpoint,
                you can use the following json payload:
              </p>
              <div className="code-block">
                <span className="code-block__title">
                  <b>JSON payload</b>
                </span>
                <button
                  className="copy"
                  onClick={() => copyTextToClipboard(JSONPayload)}>
                  Copy
                </button>
                <pre>
                  <code>
                    {`
${JSONPayload}
                    `}
                  </code>
                </pre>
              </div>
              <br />
              <div className="code-block">
                <span className="code-block__title">
                  <b>API Headers</b>
                </span>
                <button
                  className="copy"
                  onClick={() => copyTextToClipboard(APIHeader)}>
                  Copy
                </button>
                <pre>
                  <code>
                    {`
${APIHeader}
                    `}
                  </code>
                </pre>
              </div>

              <p>Expected success response:</p>
              <div className="code-block">
                <span className="code-block__title">
                  <b>Success Response</b>
                </span>
                <pre>
                  <code>
                    {`
{
    "result": "success",
    "message": {
        "assets": [
            "682030bd7b460957be870364"
        ],
        "PDFUrl": "https://tikbox1.s3.eu-west-2.amazonaws.com/assets/agreements/6800e1c340bf81d65771af7e/1746940093142.pdf",
        "assetsObj": [
            {
                "userId": "6800e1c340bf81d65771af7e",
                "originalFileName": "copyright_provenance_via_tikbox.io_tmpius9wumi_encoded.png",
                "fileUrl": "https://tikbox1.s3.eu-west-2.amazonaws.com/assets/images/6800e1c340bf81d65771af7e/copyright_provenance_via_tikbox.io_tmpius9wumi_encoded.png",
                "reference": "fcfb4fdd-ffa5-46fb-bdcc-f3c09a4f53c9",
                "fileKey": "https://tikbox1.s3.eu-west-2.amazonaws.com/assets/images/6800e1c340bf81d65771af7e/copyright_provenance_via_tikbox.io_tmpius9wumi_encoded.png",
                "fileType": "image",
                "processing": true,
                "_id": "682030bd7b460957be870364",
                "date": "2025-05-11T05:08:13.113Z",
                "__v": 0
            }
        ]
    }
                    `}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainPanel>
  )
}

const Developer = () => {
  const [loading, setLoading] = useState(false)
  const [userConfig, setUserConfig] = useState(false)

  const getUserConfig = async () => {
    setLoading(true)
    try {
      const response = await instance.get('/users/config')
      setUserConfig(response?.data?.message)
    } catch (err) {
      toast.error('Unable to generate access token')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getUserConfig()
  }, [])
  return (
    <div className="tikbox-developer">
      <div className="tikbox-developer__content">
        <SidePanel />
        {loading ? (
          <Spinner />
        ) : (
          <DeveloperContent
            userConfig={userConfig}
            setUserConfig={setUserConfig}
          />
        )}
      </div>
    </div>
  )
}

export default Developer
